import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  PencilIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Profile info state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState((user as any)?.username || '');
  const [contactNumber, setContactNumber] = useState(user?.contactNumber || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const initials = (user?.name || user?.email || 'U')
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const roleBadge: Record<string, string> = {
    ADMIN: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    RESIDENT: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    VENDOR: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    PLATFORM_ADMIN: 'bg-teal-500/20 text-teal-300 border-teal-500/40',
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileLoading(true);
    try {
      const res = await api.put('/api/auth/profile', { name, email, username, contactNumber });
      const data = res.data as { user: { name: string; email: string; username: string; contactNumber?: string } };
      updateUser(data.user);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile.' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setPasswordLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        token: user?.token,
        currentPassword,
        newPassword,
      });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const inputClass =
    'w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] transition-colors';
  const labelClass = 'block text-sm text-white/60 mb-1';

  return (
    <div className="min-h-screen bg-[#16161a] py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>

        {/* Avatar + name header */}
        <div className="flex items-center gap-5 mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0e7490] to-[#22d3ee] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
            {initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{user?.name || 'My Profile'}</h1>
            <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs border ${roleBadge[user?.role || ''] || 'bg-white/10 text-white/60 border-white/20'}`}>
              {user?.role}
            </span>
          </div>
        </div>

        {/* Profile info card */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <PencilIcon className="w-4 h-4 text-[#22d3ee]" />
            <h2 className="text-white font-semibold">Profile Information</h2>
          </div>

          <form onSubmit={handleProfileSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Full Name</label>
                <input
                  className={inputClass}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Username</label>
                <input
                  className={inputClass}
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="username"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Email</label>
                <input
                  className={inputClass}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Phone Number</label>
                <input
                  className={inputClass}
                  value={contactNumber}
                  onChange={e => setContactNumber(e.target.value)}
                  placeholder="(555) 000-0000"
                />
              </div>
            </div>

            {profileMsg && (
              <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
                profileMsg.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {profileMsg.type === 'success'
                  ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                  : <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />}
                {profileMsg.text}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={profileLoading}
                className="px-6 py-2 bg-[#0e7490] hover:bg-[#0891b2] disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {profileLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Change password card */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <LockClosedIcon className="w-4 h-4 text-[#22d3ee]" />
            <h2 className="text-white font-semibold">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordSave} className="space-y-4">
            <div>
              <label className={labelClass}>Current Password</label>
              <input
                className={inputClass}
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
                autoComplete="current-password"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>New Password</label>
                <input
                  className={inputClass}
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className={labelClass}>Confirm New Password</label>
                <input
                  className={inputClass}
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            {passwordMsg && (
              <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
                passwordMsg.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {passwordMsg.type === 'success'
                  ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                  : <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />}
                {passwordMsg.text}
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={passwordLoading}
                className="px-6 py-2 bg-[#0e7490] hover:bg-[#0891b2] disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {passwordLoading ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default Profile;
